import { Box } from '@chakra-ui/react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { useRef, useCallback, useEffect } from 'react';
import TipTapToolbar from './TipTapToolbar';

interface TipTapEditorProps {
  content: string;
  onChange: (html: string) => void;
  onImageUpload: (file: File) => Promise<string>;
  placeholder?: string;
}

export default function TipTapEditor({ content, onChange, onImageUpload, placeholder }: TipTapEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Image.configure({ inline: false, allowBase64: false }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } }),
      Placeholder.configure({ placeholder: placeholder || 'Start writing...' }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Sync external content changes (e.g. loading existing article)
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content, { emitUpdate: false });
    }
  }, [content]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImageUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    try {
      const url = await onImageUpload(file);
      editor.chain().focus().setImage({ src: url }).run();
    } catch {
      // Error handled by mutation cache
    }
    // Reset input so the same file can be uploaded again
    e.target.value = '';
  }, [editor, onImageUpload]);

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
      <TipTapToolbar editor={editor} onImageUpload={handleImageUploadClick} />
      <Box
        px={4}
        py={3}
        minH="400px"
        sx={{
          '.tiptap': {
            outline: 'none',
            minH: '380px',
          },
          '.tiptap p.is-editor-empty:first-of-type::before': {
            content: 'attr(data-placeholder)',
            color: 'gray.400',
            float: 'left',
            pointerEvents: 'none',
            height: 0,
          },
          '.tiptap h1': { fontSize: '2xl', fontWeight: 'bold', mt: 6, mb: 3 },
          '.tiptap h2': { fontSize: 'xl', fontWeight: 'semibold', mt: 5, mb: 2 },
          '.tiptap h3': { fontSize: 'lg', fontWeight: 'semibold', mt: 4, mb: 2 },
          '.tiptap p': { mb: 3, lineHeight: 1.7 },
          '.tiptap ul, .tiptap ol': { pl: 6, mb: 3 },
          '.tiptap li': { mb: 1 },
          '.tiptap blockquote': { borderLeft: '3px solid', borderColor: 'gray.300', pl: 4, my: 3, color: 'gray.600' },
          '.tiptap pre': { bg: 'gray.50', p: 3, borderRadius: 'md', overflowX: 'auto', mb: 3, fontSize: 'sm' },
          '.tiptap code': { bg: 'gray.100', px: 1, borderRadius: 'sm', fontSize: 'sm' },
          '.tiptap img': { borderRadius: 'md', my: 3, maxW: '100%' },
          '.tiptap a': { color: 'blue.500', textDecoration: 'underline' },
          '.tiptap hr': { my: 4, borderColor: 'gray.200' },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </Box>
  );
}
